import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { apiClient, handleApiError } from '@/infrastructure/api/client'
import type { Suggestion } from '@/domains/types'

export const suggestionKeys = {
  all: (sortBy?: string) => ['suggestions', sortBy] as const,
}

export function useGetSuggestions(sortBy: 'date' | 'votes' = 'votes', page = 1, limit = 10) {
  return useQuery({
    queryKey: suggestionKeys.all(sortBy),
    queryFn: async (): Promise<{ suggestions: Suggestion[]; total: number }> => {
      const response = await apiClient.get<{ suggestions: Suggestion[]; total: number }>(
        '/suggestions',
        { params: { sortBy, page, limit } },
      )
      return response.data
    },
  })
}

export function useCreateSuggestion() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (data: { title: string; description?: string }): Promise<Suggestion> => {
      const response = await apiClient.post<Suggestion>('/suggestions', data)
      return response.data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['suggestions'] })
    },
    onError: (error) => {
      throw new Error(handleApiError(error))
    },
  })
}

export function useVoteSuggestion() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (suggestionId: string): Promise<Suggestion> => {
      const response = await apiClient.post<Suggestion>(`/suggestions/${suggestionId}/vote`)
      return response.data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['suggestions'] })
    },
    onError: (error) => {
      throw new Error(handleApiError(error))
    },
  })
}

export function useDeleteSuggestion() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (entityId: string): Promise<void> => {
      await apiClient.delete(`/suggestions/${entityId}`)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['suggestions'] })
    },
    onError: (error) => {
      throw new Error(handleApiError(error))
    },
  })
}
